// Display copy for the Schedule B / HTS code-check result fields (UX redesign slice B):
// `aes_check` (accepted | replaced_with_schedule_b | not_applicable) and `code_source`
// (provided | classify_tool | aes_choice). Shared by the live card, the summary card, Saved Work
// and the PDF so all four read the same. Every row is undefined when its field is absent (a
// result classified from the description has neither), so each surface self-hides it.

export interface ScheduleBCodeRows {
  /** "8806.23.0000 (HTS)" — only when the user's own code settled the phase. */
  exportCode?: string;
  aesCheck?: string;
  codeSource?: string;
}

const AES_CHECK_LABELS: Record<string, string> = {
  accepted: 'Accepted for export reporting',
  not_applicable: 'Not needed. Schedule B codes go straight on an AES filing',
};

const CODE_SOURCE_LABELS: Record<string, string> = {
  provided: 'Your code',
  classify_tool: "Classify tool, because you didn't have a code",
  aes_choice: 'Picked from the Schedule B codes AES offers for your HTS code',
};

export const scheduleBCodeRows = (
  scheduleB: Record<string, unknown> | null | undefined,
): ScheduleBCodeRows => {
  const aes = String(scheduleB?.aes_check || '');
  const source = String(scheduleB?.code_source || '');
  const code = String(scheduleB?.schedule_b_code || '').trim();
  if (!aes && !source) {
    return {};
  }
  const hts = String(scheduleB?.normalized_code || scheduleB?.provided_code || '').trim();
  const aesCheck =
    aes === 'replaced_with_schedule_b'
      ? `Not on its own. AES uses Schedule B ${code}${hts ? ` in place of HTS ${hts}` : ''}`
      : AES_CHECK_LABELS[aes];
  return {
    exportCode: code ? `${code} (${aes === 'accepted' ? 'HTS' : 'Schedule B'})` : undefined,
    aesCheck: aesCheck || undefined,
    codeSource: CODE_SOURCE_LABELS[source] || undefined,
  };
};

const SCHEDULE_B_NA_FALLBACK = 'Not applicable to this transaction.';

const nonBlank = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim() ? value.trim() : undefined;

/** The right-panel one-line detail for the Schedule B / HTS step (Bug 6, 2026-10-06). A phase
 *  settled as not applicable (an intangible export: no product name, no code) shows its reason —
 *  the same line the chat card shows — instead of the panel's "Awaiting information..." default.
 *  Otherwise the product name, then the description; undefined when neither is known yet. */
interface ScheduleBTimelineFields {
  not_applicable?: boolean | null;
  not_applicable_reason?: string | null;
  product_name?: string | null;
  description?: string | null;
}

export const scheduleBTimelineDetail = (
  scheduleB?: ScheduleBTimelineFields | null,
): string | undefined => {
  if (scheduleB?.not_applicable) {
    return nonBlank(scheduleB?.not_applicable_reason) || SCHEDULE_B_NA_FALLBACK;
  }
  return nonBlank(scheduleB?.product_name) || nonBlank(scheduleB?.description);
};
